import { z } from "zod";
import { instantSchema, roleAtLeast, roleSchema, type Role } from "./primitives.js";

export const ERROR_CODES = [
  "validation_failed",
  "unauthenticated",
  "forbidden",
  "not_found",
  "conflict",
  "preview_stale",
  "rate_limited",
  "unsupported",
  "temporarily_unavailable",
  "resync_required",
  "internal",
] as const;
export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const ERROR_STATUS: Record<ErrorCode, number> = {
  validation_failed: 400,
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  preview_stale: 409,
  resync_required: 410,
  unsupported: 422,
  rate_limited: 429,
  internal: 500,
  temporarily_unavailable: 503,
};

export const fieldIssueSchema = z.strictObject({ path: z.string(), message: z.string() });

export const errorEnvelopeSchema = z.strictObject({
  error: z.strictObject({
    code: errorCodeSchema,
    message: z.string().min(1),
    request_id: z.string().min(1),
    issues: z.array(fieldIssueSchema).optional(),
    retry_after_seconds: z.number().int().nonnegative().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export function conflictEnvelopeSchema<T extends z.ZodType>(current: T) {
  return z.strictObject({
    error: z.strictObject({
      code: z.literal("conflict"),
      message: z.string().min(1),
      request_id: z.string().min(1),
      base_revision: z.number().int().positive(),
      current_revision: z.number().int().positive(),
      current,
      changed_by: z.string().nullable(),
      changed_at: instantSchema,
    }),
  });
}

export const revisionedMutationSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: z.string().trim().min(1),
});

export const actionAvailabilitySchema = z.discriminatedUnion("enabled", [
  z.strictObject({ action: z.string().min(1), enabled: z.literal(true) }),
  z.strictObject({
    action: z.string().min(1),
    enabled: z.literal(false),
    reason: z.string().min(1),
    required_role: roleSchema.nullable(),
  }),
]);
export type ActionAvailability = z.infer<typeof actionAvailabilitySchema>;

export function previewSchema<T extends z.ZodType>(impact: T) {
  return z.strictObject({
    preview_id: z.string().min(1),
    action: z.string().min(1),
    created_at: instantSchema,
    expires_at: instantSchema,
    basis_revision: z.number().int().nonnegative(),
    impact,
  });
}

export const confirmSchema = z.strictObject({
  preview_id: z.string().min(1),
  reason: z.string().trim().min(1),
});

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.strictObject({
    items: z.array(item),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative().nullable(),
  });
}

export const MAX_PAGE_SIZE = 200;

export const listQueryBaseSchema = z.strictObject({
  sort: z
    .string()
    .regex(/^-?[a-z_]+$/)
    .optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional(),
  selected: z.string().optional(),
  tab: z.string().optional(),
});

export type UrlState = Record<string, string | readonly string[] | undefined>;

export function encodeUrlState(state: UrlState): string {
  const params = new URLSearchParams();
  for (const key of Object.keys(state).sort()) {
    const value = state[key];
    if (value === undefined) continue;
    const values = typeof value === "string" ? [value] : [...value].sort();
    for (const v of values) if (v !== "") params.append(key, v);
  }
  return params.toString();
}

export function decodeUrlState(search: string): Record<string, string | string[]> {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const out: Record<string, string | string[]> = {};
  for (const key of new Set(params.keys())) {
    const all = params.getAll(key);
    out[key] = all.length === 1 ? (all[0] ?? "") : all;
  }
  return out;
}

export function parseUrlState<T extends z.ZodType>(
  schema: T,
  search: string,
): z.infer<T> | { invalid: true; issues: z.core.$ZodIssue[] } {
  const result = schema.safeParse(decodeUrlState(search));
  return result.success ? result.data : { invalid: true, issues: result.error.issues };
}

export const MIN_REASON_LENGTH = 3;
export const reasonSchema = z
  .string()
  .trim()
  .min(MIN_REASON_LENGTH, `A reason of at least ${String(MIN_REASON_LENGTH)} characters is required`)
  .max(2000);

export function availability(action: string, role: Role, required: Role, reason: string): ActionAvailability {
  return roleAtLeast(role, required)
    ? { action, enabled: true }
    : { action, enabled: false, reason, required_role: required };
}
